import BaseEntity from "./BaseEntity";
import GameState, { getXYindex } from "../state/gameState";
import Phaser from "phaser";
import { BOARD_SIZE, GAME_VARIABLES, layerDepthDiff } from "../constants";
import { EntityKind, MeleeEnemy, AiType } from "../state/entityKinds";
import { Dir } from "./Player";
import { RulesPush } from "../state/rules/canBePushedOnto";
import { RemovedBecause } from "../state/rules/overlap";

import data from "../artConfig.json";
import { FadeOutParticle } from "../art/FX/ForeGroundParticles";
import { SoundPlayer } from "../sounds/SoundPlayer";

export default class MeleeEnemyEntity extends BaseEntity {
  private sprite: Phaser.GameObjects.Sprite;
  private emitter: Phaser.GameObjects.Particles.ParticleEmitter;
  private turnNumber: number;
  constructor(gameState: GameState, scene: Phaser.Scene, x: number, y: number) {
    super(gameState, scene);

    this.turnNumber = 0;

    this.sprite = scene.add.sprite(
      x * BOARD_SIZE,
      y * BOARD_SIZE,
      "atlas",
      "enemy/enemy1"
    );

    const particles = scene.add.particles("atlas");
    particles.setDepth(10);
    const rect = new Phaser.Geom.Rectangle(0, 0, 4, 8);
    this.emitter = particles.createEmitter({
      quantity: { min: 1, max: 3 },
      frame: "FX/particle_shapes/particle_shapes2",
      frequency: -1,
      angle: { min: 200, max: 340 },
      speed: [5, 7, 8],
      gravityY: 2,
      lifespan: 500,
      alpha: { min: 0.1, max: 0.3 },
      emitZone: { type: "random", source: rect },
      particleClass: FadeOutParticle as any,
    });
    this.emitter.setTint(0x3f3b6c);

    this.emitter.startFollow(this.sprite, 2, 0, true);

    scene.time.delayedCall(Math.random() * 700, () => {
      this.sprite.play(data.animations.enemy_idle.key);
    });

    const entity: MeleeEnemy = {
      kind: EntityKind.MeleeEnemy,
      x,
      y,
      id: this.id,
      dir: Dir.Down,
      exhaustedTurn: false,
      aiType: AiType.NaiveChase,
      entity: () => this,
    };

    gameState.silentMutateGameState((state) => {
      this.idx = getXYindex(x, y);
      state.board[this.idx].push(entity);
    });
    if (this.entityImmutable && this.sprite) {
      this.sprite.setDepth(this.entityImmutable.y + layerDepthDiff.BOXES);
    }
  }

  afterMove() {
    if (this.entityImmutable) {
      this.sprite.setDepth(this.entityImmutable.y + layerDepthDiff.BOXES);
    } else if (this.sprite) {
      this.sprite.setDepth(
        Math.floor(this.sprite.y / 8) - 1 + layerDepthDiff.BOXES
      );
    } else {
      console.log("there is no more entity :<");
    }
  }

  // Creates the side effect of the move.
  playerMadeTurn(dirs: Dir[]): boolean {
    this.turnNumber += 1;
    const entity = this.entityImmutable as MeleeEnemy;
    if (entity.exhaustedTurn) {
      return false;
    }
    let xOffset = 0;
    if (dirs.includes(Dir.Left)) {
      xOffset = -1;
    } else if (dirs.includes(Dir.Right)) {
      xOffset = 1;
    }
    let yOffset = 0;
    if (dirs.includes(Dir.Up)) {
      yOffset = -1;
    } else if (dirs.includes(Dir.Down)) {
      yOffset = 1;
    }
    if (this.checkIfValidMove(entity.x + xOffset, entity.y + yOffset)) {
      for (const dir of dirs) {
        this.move(dir, false, true);
      }
      this.triggerEnemyMovedAnimation(dirs);
      return true;
    }
    return false;
  }

  _removeFromBoard(reason: RemovedBecause) {
    if (reason === RemovedBecause.Shot) {
      SoundPlayer.meleeBreakSFX(this.scene);
    }
    this.sprite.play(data.animations.enemy_push.key);
    super._removeFromBoard(reason);
  }

  checkIfValidMove(x: number, y: number) {
    return !this.state.turn_gameState.board[getXYindex(x, y)].some(
      (e) =>
        RulesPush.canBePushedOnto(e) !== RulesPush.OverlapResolution.CanMove
    );
  }

  move(dir: Dir, isDelayed = false, only_state = false) {
    this.moveState(dir);
    if (!only_state) {
      this.sprite.play(data.animations.enemy_push.key);
      SoundPlayer.pushMonsterSFX(this.scene);
      this.emitter.setFrequency(3);
      this.scene.time.delayedCall(GAME_VARIABLES.MOVE_TIME_MS, () => {
        this.emitter.setFrequency(-1);
      });
      this.sprite.anims.chain(data.animations.enemy_idle.key);
      this._moveTween(dir, {
        targets: this.sprite,
        delay: isDelayed ? GAME_VARIABLES.MOVE_TIME_MS / 2 : 0,
        duration: GAME_VARIABLES.MOVE_TIME_MS / 2,
      });
    }
    if (this.entityImmutable && this.sprite) {
      this.sprite.setDepth(this.entityImmutable.y + layerDepthDiff.BOXES);
    }
  }

  triggerEnemyMovedAnimation(dirs: Dir[]) {
    this.sprite.play(data.animations.enemy_jump.key);
    this.sprite.anims.chain(data.animations.enemy_idle.key);
    SoundPlayer.monsterJumpSFX(this.scene, 0.02);
    const turnDebounce = this.turnNumber + 2;
    this.scene.time.delayedCall(
      350,
      () => {
        if (turnDebounce === this.turnNumber) {
          SoundPlayer.monsterJumpSFX(this.scene);
        }
      },
      undefined,
      this
    );

    for (const dir of dirs) {
      this._moveTween(dir, {
        targets: this.sprite,
        delay: GAME_VARIABLES.MOVE_TIME_MS / 2,
        duration: GAME_VARIABLES.MOVE_TIME_MS / 2,
        ease: "Quad.easeIn",
      });
    }
  }

  moveState(dir: Dir) {
    this.state.mutateGameState((state) => {
      if (this.idx === null) {
        throw new Error("LOST TRACK OF INDEX");
      }
      const entity = state.board[this.idx].find(
        ({ id }) => id === this.id
      ) as MeleeEnemy;
      if (!entity) {
        throw new Error("Lost reference to entity! Oh no!");
      }
      if (dir === Dir.Up) {
        entity.y -= 1;
      } else if (dir === Dir.Down) {
        entity.y += 1;
      } else if (dir === Dir.Left) {
        entity.x -= 1;
      } else if (dir === Dir.Right) {
        entity.x += 1;
      }
      entity.exhaustedTurn = true;
      const last_idx = this.idx;
      this.idx = getXYindex(entity.x, entity.y);
      GameState.moveEntityOnBoard(state, entity, this.idx, last_idx);
    });
  }

  undo(c: MeleeEnemy) {
    this.scene.tweens.killTweensOf(this.sprite);
    this.sprite.alpha = 1;
    this.sprite.x = c.x * 8;
    this.sprite.y = c.y * 8;

    this.idx = getXYindex(c.x, c.y);
    this.sprite.setVisible(true);
    this.sprite.setActive(true);
    this.sprite.play(data.animations.enemy_idle.key, true);

    this.sprite.setDepth(c.y + layerDepthDiff.BOXES);
  }
}
